import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Textarea, TextInput, Tooltip } from '@mantine/core';
import {
  InfoCircle,
  LayoutAlignBottom,
  LayoutAlignCenter,
  LayoutAlignLeft,
  LayoutAlignMiddle,
  LayoutAlignRight,
  LayoutAlignTop
} from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import CustomNumberInput from '../../common/CustomNumberInput';
import { sanitizeText } from '../../../helpers/format';
import { ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM } from '../../../config/constants';

const PrintDesignEditorCanvasItemPositioningSection = ({
  canvasController,
  selectedObject,
  loading
}) => {
  const hasTextBeenEdited = useRef(false);
  const [debouncedTextValue, setDebouncedTextValue] = React.useState(null);
  const {
    setItemPosition,
    setItemProperties,
    positionObjectToTemplateById
  } = canvasController;

  const objectPositioningId = selectedObject?.containerId || selectedObject?.id;

  useEffect(() => {
    if (debouncedTextValue !== null) {
      setItemProperties(selectedObject.id, {
        ...selectedObject,
        text: debouncedTextValue
      });
    }
  }, [debouncedTextValue]);

  useEffect(() => {
    if (selectedObject) {
      const handler = setTimeout(() => {
        if (hasTextBeenEdited.current) {
          setDebouncedTextValue(selectedObject?.text ?? null);
        }
        else {
          setDebouncedTextValue(null);
        }
        hasTextBeenEdited.current = false;
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    }

    return () => {};
  }, [selectedObject?.text]);

  return (
    selectedObject && (
      <AppStack style={{ gap: 8 }}>
        {selectedObject.fkEcomPrintDesignItemType ===
          ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT && (
          <>
            <AppFlexbox style={{ flex: 1 }}>
              <TextInput
                disabled={loading}
                label="Input label"
                onChange={(e) => {
                  setItemProperties(selectedObject.id, {
                    ...selectedObject,
                    itemProperties: {
                      ...selectedObject.itemProperties,
                      inputLabel: e.currentTarget.value?.substring(0, 100) || ''
                    }
                  });
                }}
                style={{ flex: 1 }}
                value={selectedObject.itemProperties?.inputLabel || ''}
              />
            </AppFlexbox>

            <AppFlexbox style={{ flex: 1 }}>
              <CustomNumberInput
                decimalScale={0}
                disabled={loading}
                error={
                  selectedObject.itemProperties?.characterLimit <
                    selectedObject.text.length &&
                  'Character limit cannot be less than current text length'
                }
                fixedDecimalScale
                label="Character limit"
                max={1000}
                min={1}
                onBlur={() => {
                  if (
                    selectedObject.itemProperties?.characterLimit <
                    selectedObject.text.length
                  ) {
                    setItemProperties(selectedObject.id, {
                      ...selectedObject,
                      itemProperties: {
                        ...selectedObject.itemProperties,
                        characterLimit: selectedObject.text.length
                      }
                    });
                  }
                }}
                onChange={(v) => {
                  setItemProperties(selectedObject.id, {
                    ...selectedObject,
                    itemProperties: {
                      ...selectedObject.itemProperties,
                      characterLimit: v
                    }
                  });
                }}
                style={{ flex: 1 }}
                thousandSeparator
                value={selectedObject.itemProperties?.characterLimit || ''}
              />
            </AppFlexbox>

            <AppStack style={{ gap: 2 }}>
              <AppFlexbox
                style={{
                  gap: 5,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Text
                </AppText>

                <AppText style={{ fontSize: 12, color: '#666' }}>
                  {selectedObject.text.length} /{' '}
                  {selectedObject.itemProperties?.characterLimit || 0}
                </AppText>
              </AppFlexbox>

              <Textarea
                autosize
                description=""
                disabled={loading}
                label=""
                minRows={3}
                onBlur={() => {
                  if (
                    debouncedTextValue !== null &&
                    hasTextBeenEdited.current
                  ) {
                    setDebouncedTextValue(null);
                    setItemProperties(selectedObject.id, {
                      ...selectedObject,
                      text: selectedObject.text
                    });
                  }
                }}
                onChange={(e) => {
                  hasTextBeenEdited.current = true;

                  const sanitizedText = sanitizeText(e.currentTarget.value, {
                    removeLetters: selectedObject.itemProperties.noLetters,
                    removeNumbers: selectedObject.itemProperties.noNumbers,
                    removeSpecialCharacters:
                      selectedObject.itemProperties.noSpecialCharacters,
                    removeSpaces: selectedObject.itemProperties.noSpaces,
                    characterLimit:
                      selectedObject.itemProperties.characterLimit,
                    textTransform: selectedObject.itemProperties.textTransform
                  });

                  setItemProperties(
                    selectedObject.id,
                    {
                      ...selectedObject,
                      text: sanitizedText
                    },
                    true
                  );
                }}
                required
                style={{ flex: 1 }}
                value={selectedObject.text}
              />

              <AppFlexbox style={{ gap: 5 }}>
                <InfoCircle color="#666" size={20} />
                <AppStack style={{ gap: 0 }}>
                  <AppText style={{ fontSize: 12, color: '#666' }}>
                    Please use realistic placeholder text to ensure the best
                    design experience.
                  </AppText>
                </AppStack>
              </AppFlexbox>
            </AppStack>
          </>
        )}

        <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Width"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                widthInInches: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            thousandSeparator
            value={selectedObject.currentWidthInInches ?? ''}
          />
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Height"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                heightInInches: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            styles={{ controls: { padding: 0 } }}
            thousandSeparator
            value={selectedObject.currentHeightInInches ?? ''}
          />
        </AppFlexbox>
        <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
          <CustomNumberInput
            allowDecimal={false}
            disabled={loading}
            label="Rotation"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                angle: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>deg</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            thousandSeparator
            value={selectedObject.angle ?? ''}
          />

          {selectedObject.fkEcomPrintDesignItemType ===
            ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE && (
            <CustomNumberInput
              decimalScale={2}
              disabled={loading}
              fixedDecimalScale
              label="Scale"
              min={0}
              onChange={(v) => {
                setItemPosition(objectPositioningId, {
                  ...selectedObject,
                  scale: v / 100
                });
              }}
              rightSection={
                <AppStack
                  style={{
                    backgroundColor: '#F0F0F0',
                    height: '100%',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderLeft: 'solid 1px #dee2e6'
                  }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 700 }}>%</AppText>
                </AppStack>
              }
              rightSectionWidth={40}
              style={{ flex: 1 }}
              styles={{ controls: { padding: 0 } }}
              thousandSeparator
              value={selectedObject.scaleX * 100 ?? ''}
            />
          )}
        </AppFlexbox>

        <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Offset top"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                topOffset: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            styles={{ controls: { padding: 0 } }}
            thousandSeparator
            value={selectedObject.topOffset ?? ''}
          />
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Offset bottom"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                bottomOffset: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            thousandSeparator
            value={selectedObject.bottomOffset ?? ''}
          />
        </AppFlexbox>

        <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Offset left"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                leftOffset: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            styles={{ controls: { padding: 0 } }}
            thousandSeparator
            value={selectedObject.leftOffset ?? ''}
          />
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Offset right"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                rightOffset: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>in</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            thousandSeparator
            value={selectedObject.rightOffset ?? ''}
          />
        </AppFlexbox>

        <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Position top"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                topPercent: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 700 }}>%</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            styles={{ controls: { padding: 0 } }}
            thousandSeparator
            value={selectedObject.topPercent ?? ''}
          />
          <CustomNumberInput
            decimalScale={2}
            disabled={loading}
            fixedDecimalScale
            label="Position left"
            min={0}
            onChange={(v) => {
              setItemPosition(objectPositioningId, {
                ...selectedObject,
                leftPercent: v
              });
            }}
            rightSection={
              <AppStack
                style={{
                  backgroundColor: '#F0F0F0',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: 'solid 1px #dee2e6'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 700 }}>%</AppText>
              </AppStack>
            }
            rightSectionWidth={40}
            style={{ flex: 1 }}
            thousandSeparator
            value={selectedObject.leftPercent ?? ''}
          />
        </AppFlexbox>

        <AppFlexbox
          style={{ flex: 1, gap: 8, alignItems: 'center', marginTop: 4 }}
        >
          <AppFlexbox>
            <Button.Group
              style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
            >
              <Tooltip label="Align left" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(objectPositioningId, 'left');
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignLeft size={18} />
                </Button>
              </Tooltip>

              <Divider orientation="vertical" />

              <Tooltip label="Align center (horizontal)" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(
                      objectPositioningId,
                      'horizontal'
                    );
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignCenter size={18} />
                </Button>
              </Tooltip>

              <Divider orientation="vertical" />

              <Tooltip label="Align right" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(objectPositioningId, 'right');
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignRight size={18} />
                </Button>
              </Tooltip>
            </Button.Group>
          </AppFlexbox>

          <AppFlexbox>
            <Button.Group
              style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
            >
              <Tooltip label="Align top" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(objectPositioningId, 'top');
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignTop size={18} />
                </Button>
              </Tooltip>

              <Divider orientation="vertical" />

              <Tooltip label="Align center (vertical)" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(
                      objectPositioningId,
                      'vertical'
                    );
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignMiddle size={18} />
                </Button>
              </Tooltip>

              <Divider orientation="vertical" />

              <Tooltip label="Align bottom" withArrow>
                <Button
                  color="dark"
                  onClick={() => {
                    positionObjectToTemplateById(objectPositioningId, 'bottom');
                  }}
                  size="compact-md"
                  variant="subtle"
                >
                  <LayoutAlignBottom size={18} />
                </Button>
              </Tooltip>
            </Button.Group>
          </AppFlexbox>
        </AppFlexbox>
      </AppStack>
    )
  );
};

PrintDesignEditorCanvasItemPositioningSection.propTypes = {
  canvasController: PropTypes.object,
  loading: PropTypes.bool,
  selectedObject: PropTypes.object
};

export default PrintDesignEditorCanvasItemPositioningSection;
